import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/layout/PageHeader';
import { Spinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { isStillRunningError } from '../../lib/api';
import { useCurrentUser } from '../auth';
import {
  buildPlan,
  countActions,
  pageIdsToQuery,
  planToCsv,
  type Action,
  type IgFetch,
  type PlanRow,
} from '../../lib/instagramBackfill/plan';
import { applyConfirmSentence, chunk, groupUnresolvable, rowsToApply } from '../../lib/instagramBackfill/view';
import { applyChunk, fetchChunk, getBackfillAccounts, type ApplyOutcome } from './api';

// Tools → Instagram ID backfill. Brief: _architect/briefs/2026-09-28-instagram-id-backfill-tool.md
//
// THREE PHASES, deliberately: (1) the list, no token; (2) fetch the plan, token,
// writes nothing; (3) apply, admin only. The split exists to protect DIFFERS
// rows — an account that already has a DIFFERENT Instagram id is either a
// correction or a mistake, and a person decides which by ticking it.
//
// ⚠️ The plan is ASSEMBLED HERE from chunked Graph results using the same pure
// `buildPlan` the operator script uses, so a dry run here and
// `npm run backfill:instagram-account-id` produce the same rows (Download CSV
// writes the script's exact format, so the two can be diffed).
//
// ⚠️ THE TOKEN lives only in this component's state, only while a fetch is
// running or resumable, and is cleared the moment the fetch finishes. It is
// never persisted anywhere.

const PAGE_IDS_PER_REQUEST = 500;
const APPLY_ROWS_PER_REQUEST = 25;
const FETCH_ATTEMPTS = 2;

type FetchState =
  | { phase: 'idle' }
  | { phase: 'running'; queried: number; total: number }
  | { phase: 'stopped'; queried: number; total: number; error: string }
  | { phase: 'done' };

type ApplyState =
  | { phase: 'idle' }
  | { phase: 'running'; done: number; total: number }
  | { phase: 'finished'; outcomes: Record<string, ApplyOutcome>; error: string | null; counts: Record<string, number> };

const SECTION_COPY: Record<Action, { title: string; blurb: string }> = {
  'already set (DIFFERS!)': {
    title: 'Already set to a DIFFERENT Instagram account',
    blurb:
      'These accounts hold an Instagram id that is not the one Meta links to their Page. It could be a deliberate choice or a stale mistake — nothing here is written unless you tick it.',
  },
  'would set': {
    title: 'Will be set',
    blurb: 'No Instagram account stored yet; Meta returned one for the Page.',
  },
  'already set (matches)': {
    title: 'Already correct',
    blurb: 'Stored id matches Meta. A missing @name is filled in; the id is not touched.',
  },
  'no IG linked': {
    title: 'Page has no linked Instagram',
    blurb: 'Meta reports no Instagram business account on the Page. Not a fault — nothing to set.',
  },
  error: {
    title: 'Graph errors',
    blurb: 'Meta returned an error for these Pages (after retrying rate limits). Nothing is written for them.',
  },
  'no page id': { title: 'No Page to look up', blurb: '' },
};

function RowTable({
  rows,
  ticked,
  onToggle,
  applyOutcomes,
}: {
  rows: PlanRow[];
  ticked?: ReadonlySet<string>;
  onToggle?: (id: string) => void;
  applyOutcomes?: Record<string, ApplyOutcome>;
}): React.ReactElement {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-xs text-muted-foreground">
          <tr>
            {onToggle && <th className="py-1 pr-2">Overwrite</th>}
            <th className="py-1 pr-2">Account</th>
            <th className="py-1 pr-2">Facebook Page</th>
            <th className="py-1 pr-2">Stored Instagram</th>
            <th className="py-1 pr-2">Meta says</th>
            <th className="py-1 pr-2" title="The IG id cached on the linked Zernio profile — a cross-check">
              Zernio cache
            </th>
            <th className="py-1 pr-2">Detail</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const zernioDisagrees = r.zernioIgId != null && r.fetchedIgId != null && r.zernioIgId !== r.fetchedIgId;
            const outcome = applyOutcomes?.[r.id];
            return (
              <tr key={r.id} className="border-t border-border align-top">
                {onToggle && (
                  <td className="py-1.5 pr-2">
                    <Checkbox
                      checked={ticked?.has(r.id) ?? false}
                      onCheckedChange={() => onToggle(r.id)}
                      aria-label={`Overwrite ${r.name}`}
                    />
                  </td>
                )}
                <td className="py-1.5 pr-2">
                  <a className="text-kyck-600 underline underline-offset-2" href={`/accounts/${r.id}?tab=connections`}>
                    {r.name}
                  </a>
                  {outcome && <span className="ml-2 text-xs text-muted-foreground">({outcome})</span>}
                </td>
                <td className="py-1.5 pr-2 text-muted-foreground">
                  {r.pageName ?? '—'}
                  {r.pageId && <div className="font-mono text-xs">{r.pageId}</div>}
                </td>
                <td className="py-1.5 pr-2 font-mono text-xs">
                  {r.currentIgId ?? '—'}
                  {r.currentIgName && <div className="font-sans">{r.currentIgName}</div>}
                </td>
                <td className="py-1.5 pr-2 font-mono text-xs">
                  {r.fetchedIgId ?? '—'}
                  {r.fetchedIgUsername && <div className="font-sans">@{r.fetchedIgUsername.replace(/^@/, '')}</div>}
                </td>
                <td className={`py-1.5 pr-2 font-mono text-xs ${zernioDisagrees ? 'text-amber-700 font-semibold' : ''}`}>
                  {r.zernioIgId ?? '—'}
                </td>
                <td className="py-1.5 pr-2 text-xs text-muted-foreground">{r.detail ?? ''}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Collapsible({
  title,
  count,
  defaultOpen,
  tone,
  children,
}: {
  title: string;
  count: number;
  defaultOpen: boolean;
  tone?: 'warn';
  children: React.ReactNode;
}): React.ReactElement | null {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;
  return (
    <section
      className={`mb-4 rounded-md border p-3 ${tone === 'warn' ? 'border-amber-400 bg-amber-50' : 'border-border'}`}
    >
      <button
        type="button"
        className="flex w-full items-baseline justify-between text-left"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="font-medium text-foreground">
          {title} <span className="text-muted-foreground">({count})</span>
        </span>
        <span className="text-xs text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </section>
  );
}

function downloadCsv(rows: PlanRow[]): void {
  const url = URL.createObjectURL(new Blob([planToCsv(rows)], { type: 'text/csv' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = 'backfill-instagram-account-id.report.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function InstagramBackfillPage(): React.ReactElement {
  const me = useCurrentUser();
  const isAdmin = me.role === 'admin';
  const queryClient = useQueryClient();
  const accountsQuery = useQuery({
    queryKey: ['instagram-backfill-accounts'],
    queryFn: getBackfillAccounts,
  });
  const accounts = accountsQuery.data?.accounts ?? null;

  const [token, setToken] = useState('');
  const [results, setResults] = useState<Map<string, IgFetch>>(new Map());
  const [fetchState, setFetchState] = useState<FetchState>({ phase: 'idle' });
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [applyState, setApplyState] = useState<ApplyState>({ phase: 'idle' });
  const [confirming, setConfirming] = useState(false);

  const unresolvable = useMemo(() => (accounts ? groupUnresolvable(accounts) : []), [accounts]);
  const allPageIds = useMemo(() => (accounts ? pageIdsToQuery(accounts) : []), [accounts]);
  const plan = useMemo(() => (accounts && results.size > 0 ? buildPlan(accounts, results) : null), [accounts, results]);
  const counts = useMemo(() => (plan ? countActions(plan.rows) : null), [plan]);
  const byAction = (a: Action): PlanRow[] => plan?.rows.filter((r) => r.action === a) ?? [];
  const applyPreview = useMemo(() => (plan ? rowsToApply(plan.rows, ticked) : null), [plan, ticked]);

  async function runFetch(): Promise<void> {
    if (!token.trim()) return;
    let remaining = allPageIds.filter((p) => !results.has(p));
    const total = allPageIds.length;
    let merged = new Map(results);
    setFetchState({ phase: 'running', queried: merged.size, total });
    let failures = 0;
    while (remaining.length > 0) {
      const batch = remaining.slice(0, PAGE_IDS_PER_REQUEST);
      try {
        const resp = await fetchChunk(token.trim(), batch);
        merged = new Map(merged);
        for (const { pageId, result } of resp.results) merged.set(pageId, result);
        setResults(merged);
        remaining = [...resp.remaining, ...remaining.slice(PAGE_IDS_PER_REQUEST)];
        failures = 0;
        setFetchState({ phase: 'running', queried: merged.size, total });
      } catch (err) {
        // ⚠️ Read-only, so a lost chunk (including a 524) is simply asked
        // again. After a second failure we stop and offer Resume rather than
        // looping — a revoked token fails every chunk the same way.
        failures++;
        if (failures >= FETCH_ATTEMPTS) {
          setFetchState({
            phase: 'stopped',
            queried: merged.size,
            total,
            error: (err as Error).message || 'The request failed.',
          });
          return;
        }
      }
    }
    setFetchState({ phase: 'done' });
    setToken(''); // ⚠️ discard it: nothing after this needs it.
  }

  async function runApply(): Promise<void> {
    if (!applyPreview || applyPreview.rows.length === 0) return;
    setConfirming(false);
    const runId = crypto.randomUUID();
    const chunks = chunk(applyPreview.rows, APPLY_ROWS_PER_REQUEST);
    const outcomes: Record<string, ApplyOutcome> = {};
    let done = 0;
    let error: string | null = null;
    setApplyState({ phase: 'running', done, total: applyPreview.rows.length });
    for (const rows of chunks) {
      const overwriteIds = rows
        .filter((r) => r.action === 'already set (DIFFERS!)' && ticked.has(r.id))
        .map((r) => r.id);
      try {
        const resp = await applyChunk(runId, rows, overwriteIds);
        for (const o of resp.outcomes) outcomes[o.accountId] = o.outcome;
        done += rows.length;
        setApplyState({ phase: 'running', done, total: applyPreview.rows.length });
      } catch (err) {
        // ⚠️ STOP on any failure. Each account in a chunk is its own
        // transaction, so the accounts before the failure are written and
        // the rest untouched; the list is reloaded below so what you see is
        // the live state, not the plan's. (A re-apply is safe — a written row
        // re-classifies as "already correct" — but a person should look first.)
        error = isStillRunningError(err)
          ? 'The connection timed out while writing. Some accounts in the last batch may have been written — the list below has been reloaded from the live data. Check it before applying again.'
          : (err as Error).message || 'The request failed.';
        break;
      }
    }
    const tally: Record<string, number> = {};
    for (const o of Object.values(outcomes)) tally[o] = (tally[o] ?? 0) + 1;
    setApplyState({ phase: 'finished', outcomes, error, counts: tally });
    setTicked(new Set());
    // Re-read the live accounts: the plan rebuilds from the same Graph results
    // against the NEW stored values, so written rows move to "Already correct".
    await queryClient.invalidateQueries({ queryKey: ['instagram-backfill-accounts'] });
  }

  const toggle = (id: string): void =>
    setTicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const running = fetchState.phase === 'running' || applyState.phase === 'running';

  return (
    <>
      <PageHeader
        title="Instagram ID backfill"
        description="Fill each account's default Instagram account from the Instagram business account Meta links to its Facebook Page."
      />

      {accountsQuery.isLoading && <Spinner />}
      {accountsQuery.error && <p className="text-sm text-rose-600">Could not load the account list.</p>}

      {accounts && (
        <>
          {/* ── 1. THE LIST ───────────────────────────────────────── */}
          <section className="mb-6">
            <h2 className="mb-1 text-base font-semibold">1. The fleet</h2>
            <p className="text-sm text-muted-foreground">
              {accounts.length} accounts · {accounts.filter((a) => a.currentIgId).length} already have an Instagram
              account stored · {accounts.filter((a) => a.pageId).length} have a Facebook Page to look up (
              {allPageIds.length} distinct Pages).
            </p>
            {unresolvable.length > 0 && (
              <Collapsible
                title="Can't be looked up — no Facebook Page on record"
                count={unresolvable.reduce((n, g) => n + g.accounts.length, 0)}
                defaultOpen={false}
              >
                {unresolvable.map((g) => (
                  <div key={g.note} className="mb-3">
                    <p className="text-sm font-medium">
                      {g.note} <span className="text-muted-foreground">({g.accounts.length})</span>
                    </p>
                    <ul className="mt-0.5 columns-2 text-xs text-muted-foreground">
                      {g.accounts.map((a) => (
                        <li key={a.id}>
                          <a className="underline underline-offset-2" href={`/accounts/${a.id}?tab=connections`}>
                            {a.name}
                          </a>
                          {a.currentIgId && <span> — has {a.currentIgId}</span>}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </Collapsible>
            )}
          </section>

          {/* ── 2. FETCH ──────────────────────────────────────────── */}
          <section className="mb-6">
            <h2 className="mb-1 text-base font-semibold">2. Fetch the plan from Meta</h2>
            <p className="text-sm text-muted-foreground">
              Paste a Graph API user token with <code>pages_read_engagement</code>, <code>instagram_basic</code> and{' '}
              <code>business_management</code> (the Graph API Explorer can issue one). It is used for this run only
              and never stored. This step writes nothing.
            </p>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Input
                type="password"
                autoComplete="off"
                spellCheck={false}
                className="max-w-md"
                placeholder="Graph API token"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                disabled={running}
                aria-label="Graph API token"
              />
              <Button onClick={() => void runFetch()} disabled={running || !token.trim() || allPageIds.length === 0}>
                {fetchState.phase === 'stopped' ? 'Resume' : results.size > 0 ? 'Fetch again (remaining)' : 'Fetch plan'}
              </Button>
              {results.size > 0 && !running && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setResults(new Map());
                    setTicked(new Set());
                    setFetchState({ phase: 'idle' });
                    setApplyState({ phase: 'idle' });
                  }}
                >
                  Start over
                </Button>
              )}
            </div>
            {fetchState.phase === 'running' && (
              <div className="mt-2 flex items-center gap-2 text-sm">
                <Spinner className="h-4 w-4" /> {fetchState.queried} of {fetchState.total} Pages queried…
              </div>
            )}
            {fetchState.phase === 'stopped' && (
              <p className="mt-2 text-sm text-rose-600">
                Stopped after {fetchState.queried} of {fetchState.total} Pages: {fetchState.error} Nothing was written.
                Check the token and press Resume — Pages already queried are kept.
              </p>
            )}
          </section>

          {/* ── THE PLAN ──────────────────────────────────────────── */}
          {plan && counts && (
            <section className="mb-6">
              <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                <h2 className="text-base font-semibold">The plan</h2>
                <Button variant="outline" size="sm" onClick={() => downloadCsv(plan.rows)}>
                  Download CSV
                </Button>
              </div>
              <p className="mb-3 text-sm text-muted-foreground">
                {counts['would set']} to set · {counts['already set (DIFFERS!)']} differ · {counts['already set (matches)']}{' '}
                already correct · {counts['no IG linked']} no Instagram linked · {counts.error} errors ·{' '}
                {counts['no page id']} no Page
                {plan.pendingAccountIds.length > 0 && ` · ${plan.pendingAccountIds.length} still to query`}
              </p>

              <Collapsible
                title={SECTION_COPY['already set (DIFFERS!)'].title}
                count={counts['already set (DIFFERS!)']}
                defaultOpen
                tone="warn"
              >
                <p className="mb-2 text-sm">{SECTION_COPY['already set (DIFFERS!)'].blurb}</p>
                <RowTable
                  rows={byAction('already set (DIFFERS!)')}
                  ticked={ticked}
                  onToggle={isAdmin ? toggle : undefined}
                  applyOutcomes={applyState.phase === 'finished' ? applyState.outcomes : undefined}
                />
              </Collapsible>

              {(['would set', 'error', 'already set (matches)', 'no IG linked'] as const).map((a) => (
                <Collapsible
                  key={a}
                  title={SECTION_COPY[a].title}
                  count={counts[a]}
                  defaultOpen={a === 'would set' || a === 'error'}
                >
                  <p className="mb-2 text-sm text-muted-foreground">{SECTION_COPY[a].blurb}</p>
                  <RowTable
                    rows={byAction(a)}
                    applyOutcomes={applyState.phase === 'finished' ? applyState.outcomes : undefined}
                  />
                </Collapsible>
              ))}
            </section>
          )}

          {/* ── 3. APPLY ──────────────────────────────────────────── */}
          {plan && applyPreview && (
            <section className="mb-6">
              <h2 className="mb-1 text-base font-semibold">3. Apply</h2>
              {!isAdmin ? (
                <p className="text-sm text-muted-foreground">Only an admin can write these changes.</p>
              ) : (
                <>
                  <p className="text-sm text-muted-foreground">{applyConfirmSentence(applyPreview)}</p>
                  {plan.pendingAccountIds.length > 0 && (
                    <p className="text-sm text-amber-700">
                      {plan.pendingAccountIds.length} accounts have not been queried yet; they are left out.
                    </p>
                  )}
                  <Button
                    className="mt-2"
                    onClick={() => setConfirming(true)}
                    disabled={running || applyPreview.rows.length === 0}
                  >
                    Apply {applyPreview.rows.length} change{applyPreview.rows.length === 1 ? '' : 's'}
                  </Button>
                </>
              )}
              {applyState.phase === 'running' && (
                <div className="mt-2 flex items-center gap-2 text-sm">
                  <Spinner className="h-4 w-4" /> Writing… {applyState.done} of {applyState.total}
                </div>
              )}
              {applyState.phase === 'finished' && (
                <div className="mt-2 text-sm">
                  <p>
                    Done:{' '}
                    {Object.entries(applyState.counts)
                      .map(([k, v]) => `${v} ${k}`)
                      .join(' · ') || 'nothing written'}
                    .
                  </p>
                  {(applyState.counts['raced-differs'] ?? 0) > 0 && (
                    <p className="text-amber-700">
                      “raced-differs” means the account gained a different Instagram id after the plan was fetched,
                      so it was left alone.
                    </p>
                  )}
                  {applyState.error && <p className="text-rose-600">{applyState.error}</p>}
                </div>
              )}
            </section>
          )}

          <Dialog open={confirming} onOpenChange={setConfirming}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Write to live accounts?</DialogTitle>
                <DialogDescription>
                  {applyPreview ? applyConfirmSentence(applyPreview) : ''} Each account is written and audited one at a
                  time; accounts that change after the plan was fetched are re-checked and left alone if they now
                  differ.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
                <Button onClick={() => void runApply()}>Write</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      )}
    </>
  );
}
